import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Copy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";

const example = (endpoint, key) => `curl -X POST "${endpoint}" \\
  -H "X-Api-Key: ${key || "<CHIAVE>"}" -H "Content-Type: application/json" \\
  -d '{"project_code": "<CODICE COMMESSA>", "service_type": "DELIVERY",
       "orders": [{"external_order_id": "ORD123", "executing_company": "EASYWEB",
                   "customer_name": "Mario Rossi", "phone": "3330000000",
                   "street": "Via Roma", "number": "1", "city": "Milano", "province": "MI",
                   "technology": "FTTH", "notes": ""}]}'`;

const SourceKey = ({ s, onKey }) => {
  const gen = async () => {
    if (s.api_key_hint && !window.confirm("Generare una nuova chiave? Quella attuale smetterà di funzionare.")) return;
    try { const r = await api.post(`/integration-sources/${s.id}/api-key`); onKey(s.id, r.data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5 text-sm" data-testid={`api-source-${s.id}`}>
      <span><b>{s.name}</b> <span className="text-xs text-muted-foreground">{s.api_key_hint ? `chiave attiva …${s.api_key_hint} · ${fmtDateTime(s.api_key_at)}` : "nessuna chiave"}</span></span>
      <Button size="sm" variant="outline" onClick={gen} data-testid={`api-key-generate-${s.id}`}><KeyRound className="mr-1 h-4 w-4" />{s.api_key_hint ? "Rigenera chiave" : "Genera chiave"}</Button>
    </div>
  );
};

export const ApiSection = () => {
  const [sources, setSources] = useState([]);
  const [shown, setShown] = useState(null);
  const load = () => api.get("/integration-sources").then((r) => setSources(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const onKey = (id, data) => { setShown({ id, ...data }); load(); };
  const endpoint = shown?.endpoint || `${process.env.REACT_APP_BACKEND_URL}/api/inbound/orders`;
  return (
    <section className="mb-8" data-testid="auto-import-api">
      <h2 className="mb-1 font-display text-lg font-bold">API: un sistema esterno ci invia gli ordini</h2>
      <p className="mb-3 text-sm text-muted-foreground">Ogni fonte ha la sua chiave segreta. Gli ordini entrano subito da pianificare, divisi per azienda esecutrice (campo <span className="font-mono">executing_company</span>: codice o ragione sociale); stesse regole di duplicati e aggiornamenti dell'import manuale.</p>
      <div className="border bg-white">{sources.map((s) => <SourceKey key={s.id} s={s} onKey={onKey} />)}</div>
      {shown && <div className="mt-3 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm" data-testid="api-key-shown">
        <b>Copia ora la chiave: non sarà più mostrata.</b>
        <div className="mt-1 flex items-center gap-2"><code className="break-all bg-white px-2 py-1 text-xs" data-testid="api-key-value">{shown.api_key}</code>
          <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard?.writeText(shown.api_key); toast.success("Chiave copiata"); }} data-testid="api-key-copy"><Copy className="h-4 w-4" /></Button></div>
      </div>}
      <div className="micro-label mb-1 mt-4">Esempio di chiamata</div>
      <pre className="overflow-x-auto bg-foreground p-3 text-[11px] leading-relaxed text-background" data-testid="api-example">{example(endpoint, shown?.api_key)}</pre>
      <p className="mt-2 text-xs text-muted-foreground">Campi accettati: external_order_id e city obbligatori; executing_company, customer_name, phone, street, number, staircase, floor, apartment, province, region, technology, priority, notes, appointment_date e gli altri campi dell'import. Risposta: conteggi e esito riga per riga.</p>
    </section>
  );
};
